import { env } from './env';
import { readCache, writeCache, invalidate } from './cache';
import { rememberPhone } from './last-phone';
import { enqueue } from './sync';
import { newIdempotencyKey } from './sync/idempotency';
import type { QueueItem } from './sync/queue';

export type ApiError = { status: number; code?: string; message: string };

/**
 * Throws an ApiError-shaped object on every failure path:
 *  - HTTP non-2xx: status + optional code + message from the response body
 *  - Network failure (offline, DNS, CORS): status: 0, code: 'NETWORK_ERROR'
 *
 * Callers can branch uniformly on `err.status` (0 = offline banner,
 * 401 = auth gate, 403 = feature flag / inactive, 4xx/5xx = generic toast).
 */
export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const base = env().VITE_API_BASE.replace(/\/+$/, '');
  const url = `${base}/v1${path}`;

  // Hard timeout so a stalled request on a weak mobile connection surfaces as
  // a retryable error instead of an infinite spinner (the onboarding-submit
  // hang). Large bodies (base64 doc/photo uploads) get a longer window.
  const bodyBytes = typeof init.body === 'string' ? init.body.length : 0;
  const timeoutMs = bodyBytes > 200_000 ? 120_000 : 30_000;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let res: Response;
  try {
    res = await fetch(url, {
      ...init,
      credentials: 'include',
      signal: init.signal ?? controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(init.headers || {}),
      },
    });
  } catch (networkErr) {
    // fetch() throws TypeError on offline / DNS / CORS, AbortError on timeout —
    // wrap so callers see a uniform ApiError shape instead of two error types.
    const aborted = (networkErr as Error)?.name === 'AbortError';
    const err: ApiError = {
      status: 0,
      code: aborted ? 'TIMEOUT' : 'NETWORK_ERROR',
      message: aborted
        ? 'The request took too long. Check your connection and try again.'
        : (networkErr as Error).message || 'Network error. Check your connection and try again.',
    };
    throw err;
  } finally {
    clearTimeout(timer);
  }

  if (!res.ok) {
    let body: any = null;
    try { body = await res.json(); } catch { /* noop */ }
    const err: ApiError = {
      status: res.status,
      code: body?.code,
      message: body?.message || res.statusText,
    };
    throw err;
  }

  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}

/**
 * Mutating-write wrapper. Attempts an online write first; on offline (status:0)
 * or 5xx, queues the mutation in IDB for later drain. Returns the response on
 * success, throws on 4xx (except offline), or returns { queued: true } on queue.
 */
export async function mutateOrQueue<T = unknown>(
  path: string,
  init: {
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    body?: unknown;
    op: QueueItem['op'];
    invalidatesOnSuccess?: string[];
  },
): Promise<{ data: T; queued: false } | { data: null; queued: true; queueItemId: string }> {
  // One logical mutation gets one key even when the online response is lost
  // and the same write falls back to the durable queue.
  const idempotencyKey = newIdempotencyKey();
  try {
    const data = await apiFetch<T>(path, {
      method: init.method,
      body: init.body ? JSON.stringify(init.body) : undefined,
      headers: { 'Idempotency-Key': idempotencyKey },
    });
    for (const pattern of init.invalidatesOnSuccess ?? []) {
      await invalidate(pattern);
    }
    return { data, queued: false };
  } catch (e: any) {
    if (e?.status === 0 || (typeof e?.status === 'number' && e.status >= 500)) {
      const item = await enqueue({
        idempotencyKey,
        op: init.op,
        url: path,
        method: init.method,
        body: init.body,
        invalidatesOnSuccess: init.invalidatesOnSuccess,
      });
      return { data: null, queued: true, queueItemId: item.id };
    }
    throw e;
  }
}

/**
 * Redeem a one-time invite code issued by the operator. On success the backend
 * sets an auth cookie and returns the driver's public profile, transitioning
 * their account from PENDING → ACTIVE. The code is burned on first use.
 * Phone is required since 2026-05-18: the backend now gates OTP login behind
 * activation, so redeemCode is also the first-time activation path.
 */
export async function redeemInviteCode(phone: string, code: string): Promise<{ driver: import('./auth').DriverProfile }> {
  const res = await apiFetch<{ driver: import('./auth').DriverProfile }>('/driver-auth/redeem-code', {
    method: 'POST',
    body: JSON.stringify({ phone, code }),
  });
  // Remember the number so a later dropped session prefills the OTP recovery
  // screen instead of forcing a full re-type (GH #502).
  rememberPhone(phone);
  return res;
}

/**
 * GET wrapper that consults the cache first. Behavior:
 * - fresh entry → return cached, no network
 * - stale entry → return cached AND start a background refetch (caller can re-read on next render)
 * - expired/missing → network only
 *
 * Returns { data, fromCache, state }.
 * Callers should use the `state` to decide whether to show the stale banner.
 */
export async function cachedGet<T = unknown>(
  path: string,
  options: { forceNetwork?: boolean } = {},
): Promise<{ data: T; fromCache: boolean; state: 'fresh' | 'stale' | 'network' }> {
  const base = env().VITE_API_BASE.replace(/\/+$/, '');
  const url = `${base}/v1${path}`;

  const cached = options.forceNetwork ? null : await readCache<T>(url);
  if (cached?.state === 'fresh') {
    return { data: cached.data, fromCache: true, state: 'fresh' };
  }
  if (cached?.state === 'stale') {
    // Kick off background refresh (fire & forget; subsequent reads will see fresh)
    apiFetch<T>(path)
      .then((fresh) => writeCache(url, fresh))
      .catch(() => {});
    return { data: cached.data, fromCache: true, state: 'stale' };
  }
  // Network read
  const data = await apiFetch<T>(path);
  await writeCache(url, data);
  return { data, fromCache: false, state: 'network' };
}
